import { ErrorResponse, HttpMethod } from '@aibees/shared/definition';
import { useBaseQuery } from '@aibees/shared/http';
import { useWorkspace } from '@aibees/shared/workspace';
import { UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { COMPANY_READ_API_PREFIX } from '../../constants';
import {
  CompanyChangesSocketMessage,
  OnReceiveSocketCompanyChangesEvent,
  ReadCompanyRequest,
  ReadCompanyResponse,
} from '../types';
import { useReadCompanyChanges } from './use-read-company-changes';
import { useSocketCompanyChanges } from './use-socket-company-changes';

export function useReadCompany(id?: string) {
  const workspaceId = useWorkspace()?.id;
  const companyChangesApi = useReadCompanyChanges(id);

  const api = useBaseQuery<ReadCompanyRequest, ReadCompanyResponse>(
    [useReadCompany.name, id],
    {
      url: COMPANY_READ_API_PREFIX.replace(':wid', String(workspaceId)).replace(
        ':id',
        String(id)
      ),
      method: HttpMethod.get,
      headers: { 'x-customer-id': workspaceId },
    },
    {
      enabled: !!id,
    }
  );

  const updatedData = useMemo(
    () =>
      companyChangesApi.data && companyChangesApi.data.id === api.data?.id
        ? { ...api.data, ...companyChangesApi.data }
        : api.data,
    [companyChangesApi.data, api]
  );

  // refetch the data on related socket events
  const onEvent: OnReceiveSocketCompanyChangesEvent = (
    data: CompanyChangesSocketMessage
  ) => {
    companyChangesApi.debouncedRefetch();
  };

  useSocketCompanyChanges(onEvent);

  return useMemo(() => {
    return { ...api, ...{ data: updatedData } } as UseQueryResult<
      ReadCompanyResponse,
      ErrorResponse
    >;
  }, [updatedData, api]);
}
